
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "../App.css";
import {
  deleteAllIdpConfigs,
  deleteAllTenantMappings,
  ensureTenantMappingForIdp,
  listIdpConfigs,
  upsertIdpConfig,
} from "../components/adminApi";

import {
  PROVIDER_OPTIONS,
  formFromSaved,
  presetFor,
} from "../components/idpPresets";

function ConfigPage() {
  const [form, setForm] = useState(() => presetFor("KEYCLOAK"));
  const [saved, setSaved] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const selected = PROVIDER_OPTIONS.find(
    (p) => p.value === form.providerType
  );

  const ready = selected?.ready ?? false;

  const existingForTenant = saved.find(
    (item) => item.tenantKey === form.tenantKey.trim()
  );

  const willUpdate = Boolean(editingId || existingForTenant);

  async function refreshSaved() {
    const data = await listIdpConfigs();
    setSaved(data);
  }

  useEffect(() => {
    void refreshSaved().catch((err) => {
      setError(
        err instanceof Error ? err.message : "Failed to load configs"
      );
    });
  }, []);

  function update(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function onProviderChange(provider) {
    setForm(presetFor(provider));
    setEditingId(null);
    setMessage(null);
    setError(null);
  }

  function loadSaved(item) {
    setForm(formFromSaved(item));
    setEditingId(item.id);

    setMessage(
      `Loaded "${item.name}" for editing. Leave client secret blank to keep the existing secret.`
    );

    setError(null);
  }

  async function handleSave() {
    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      if (!ready) {
        throw new Error(
          `${selected?.label ?? form.providerType} is not enabled  yet.`
        );
      }

      const { saved: result, created } = await upsertIdpConfig(form);

      try {
        await ensureTenantMappingForIdp(form);
      } catch (mappingErr) {
        console.warn(mappingErr);
      }

      setEditingId(result.id);

      await refreshSaved();

      setMessage(
        created
          ? `Created IdP "${result.name}" (${result.tenantKey}). You can sign in from Home.`
          : `Updated IdP "${result.name}" (${result.tenantKey}).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleClearAll() {
    setBusy(true);
    setMessage(null);
    setError(null);

    try {
      const idpCount = await deleteAllIdpConfigs();
      const mappingCount = await deleteAllTenantMappings();

      setEditingId(null);

      await refreshSaved();

      setMessage(
        `Deleted ${idpCount} IdP config(s) and ${mappingCount} tenant mapping(s).`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="page wide">
      <header className="hero">
        <p className="brand">CestaConnect SSO</p>

        <h1>IdP configuration</h1>

        <p className="lede">
          Pick a provider, review the pre-filled local values, then Save.
          Saving updates an existing config when the tenant key already
          exists. Fields change based on the dropdown (Keycloak, Entra ID,
          and AWS Cognito ready).
        </p>

        <p className="nav">
          <Link to="/">← Back to login</Link>
        </p>
      </header>

      <section className="panel">
        <label className="field">
          <span>SSO provider</span>

          <select
            value={form.providerType}
            onChange={(e) =>
              onProviderChange(e.target.value)
            }
          >
            {PROVIDER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
                {opt.ready ? "" : " (coming soon)"}
              </option>
            ))}
          </select>
        </label>

        {!ready ? (
          <p className="hint">
            {selected?.label} fields are shown for preview. Saving is
            disabled until that provider flow is finished.
          </p>
        ) : null}

        {willUpdate ? (
          <p className="hint">
            Tenant key <code>{form.tenantKey}</code> already exists — Save
            will update it
            {form.clientSecret.trim()
              ? " and replace the client secret."
              : " (leave client secret blank to keep the current one)."}
          </p>
        ) : null}

        <div className="form-grid">
          <Field
            label="Name"
            value={form.name}
            onChange={(v) => update("name", v)}
          />

          <Field
            label="Tenant key"
            value={form.tenantKey}
            onChange={(v) => update("tenantKey", v)}
          />

          <label className="field">
            <span>Auth mode</span>

            <select
              value={form.authMode}
              onChange={(e) =>
                update("authMode", e.target.value)
              }
            >
              <option value="LOCAL_JWT">LOCAL_JWT</option>
              <option value="HANDOVER">HANDOVER</option>
            </select>
          </label>

          <label className="field checkbox">
            <span>Enabled</span>

            <input
              type="checkbox"
              checked={form.enabled}
              onChange={(e) =>
                update("enabled", e.target.checked)
              }
            />
          </label>

          <Field
            label="Client ID"
            value={form.clientId}
            onChange={(v) => update("clientId", v)}
          />

          <Field
            label="Client secret"
            value={form.clientSecret}
            onChange={(v) => update("clientSecret", v)}
            type="password"
          />

          <Field
            label="Scopes (comma-separated)"
            value={form.scopes}
            onChange={(v) => update("scopes", v)}
            className="span-2"
          />

          <Field
            label="Redirect URI (SSO callback)"
            value={form.redirectUri}
            onChange={(v) => update("redirectUri", v)}
            className="span-2"
          />

          <Field
            label="Post-login redirect URI (app)"
            value={form.postLoginRedirectUri}
            onChange={(v) =>
              update("postLoginRedirectUri", v)
            }
            className="span-2"
          />

          {/* KEYCLOAK */}
          {form.providerType === "KEYCLOAK" ? (
            <>
              <Field
                label="Issuer URI"
                value={form.issuerUri}
                onChange={(v) => update("issuerUri", v)}
                className="span-2"
              />

              <Field
                label="Realm"
                value={form.realm}
                onChange={(v) => update("realm", v)}
              />

              <Field
                label="Roles claim"
                value={form.rolesClaim}
                onChange={(v) => update("rolesClaim", v)}
              />

              <Field
                label="End session endpoint"
                value={form.endSessionEndpoint}
                onChange={(v) =>
                  update("endSessionEndpoint", v)
                }
                className="span-2"
              />
            </>
          ) : null}

          {/* ENTRA ID */}
          {form.providerType === "ENTRA_ID" ? (
            <>
              <Field
                label="Directory (tenant) ID"
                value={form.tenantId}
                onChange={(v) => update("tenantId", v)}
                className="span-2"
              />

              <Field
                label="Issuer URI"
                value={form.issuerUri}
                onChange={(v) => update("issuerUri", v)}
                className="span-2"
              />

              <Field
                label="Roles claim"
                value={form.rolesClaim}
                onChange={(v) => update("rolesClaim", v)}
              />

              <Field
                label="End session endpoint"
                value={form.endSessionEndpoint}
                onChange={(v) =>
                  update("endSessionEndpoint", v)
                }
                className="span-2"
              />

              <p className="hint span-2">
                Fill tenant ID, application (client) ID, and
                client secret from the Entra App registration.
                Issuer / logout URLs can keep{" "}
                <code>{"{tenantId}"}</code> — it is substituted on
                save.
              </p>
            </>
          ) : null}

          {/* AWS COGNITO */}
          {form.providerType === "COGNITO" ? (
            <>
              <Field
                label="Issuer URI"
                value={form.issuerUri}
                onChange={(v) => update("issuerUri", v)}
                className="span-2"
              />

              <Field
                label="Region"
                value={form.region}
                onChange={(v) => update("region", v)}
              />

              <Field
                label="User pool ID"
                value={form.userPoolId}
                onChange={(v) =>
                  update("userPoolId", v)
                }
              />

              <Field
                label="Cognito domain"
                value={form.domain}
                onChange={(v) => update("domain", v)}
                className="span-2"
              />

              <Field
                label="Roles claim"
                value={form.rolesClaim}
                onChange={(v) =>
                  update("rolesClaim", v)
                }
                className="span-2"
              />

              <p className="hint span-2">
                Fill AWS Region, User pool ID, and Cognito
                domain. Select Auth mode (LOCAL_JWT for SSO-minted
                tokens or HANDOVER for client callback).
              </p>
            </>
          ) : null}

          {/* OTHER PROVIDERS */}
          {form.providerType !== "KEYCLOAK" &&
          form.providerType !== "ENTRA_ID" &&
          form.providerType !== "COGNITO" ? (
            <Field
              label="Issuer URI"
              value={form.issuerUri}
              onChange={(v) => update("issuerUri", v)}
              className="span-2"
            />
          ) : null}
        </div>

        {message ? (
          <p className="status ok">{message}</p>
        ) : null}

        {error ? (
          <p className="status bad">{error}</p>
        ) : null}

        <div className="actions">
          <button
            type="button"
            disabled={busy || !ready}
            onClick={() => void handleSave()}
          >
            {busy ? "Working…" : willUpdate ? "Update" : "Save"}
          </button>

          <button
            type="button"
            className="secondary"
            disabled={busy}
            onClick={() => {
              setForm(presetFor(form.providerType));
              setEditingId(null);
              setMessage("Form reset to local preset.");
              setError(null);
            }}
          >
            Reset preset
          </button>

          <button
            type="button"
            className="secondary danger"
            disabled={busy}
            onClick={() => void handleClearAll()}
          >
            Delete all saved configs
          </button>
        </div>
      </section>

      {/* SAVED CONFIGURATIONS */}
      <section className="panel">
        <h2 className="section-title">
          Saved in MongoDB
        </h2>

        {saved.length === 0 ? (
          <p className="hint">
            No IdP configs yet. Click Save to create one.
          </p>
        ) : (
          <ul className="saved-list">
            {saved.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="linkish"
                  disabled={busy}
                  onClick={() => loadSaved(item)}
                >
                  <strong>{item.name}</strong>

                  <span className="mono">
                    {" "}
                    {item.tenantKey}
                  </span>
                </button>

                <span>
                  {" "}
                  · {item.providerType}
                </span>

                <span>
                  {item.enabled
                    ? " · enabled"
                    : " · disabled"}
                </span>

                {editingId === item.id ? (
                  <span> · editing</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  className = "",
}) {
  return (
    <label className={`field ${className}`.trim()}>
      <span>{label}</span>

      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export default ConfigPage;
